import React from "react";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import MediaGallery from "@bmi-digital/components/media-gallery";
import { useSiteContext } from "../../../components/Site";
import AssetsIframe from "../../../components/AssetsIframe";
import { transformImages } from "../../../utils/product-details-transforms";
import withGTM from "../../../utils/google-tag-manager";
import {
  classes,
  StyledAccordion,
  StyledAccordionItem,
  StyledAccordionDetails,
  StyledAccordionSummary
} from "../../../components/styles/ProductLeadBlock.styles";
import AboutTabContent from "./about-tab-content";
import TechnicalSpecifications from "./technical-specifications";
import ProductDocuments from "./product-documents";
import SpecificationIframe from "./specification-iframe";
import FixingToolIframe from "./fixing-tool-iframe";
import type { AccordionSummaryProps } from "@bmi-digital/components/accordion";
import type { ProductLeadBlockProps } from "../types";

const GTMAccordionSummary = withGTM<AccordionSummaryProps>(
  StyledAccordionSummary
);

const MobileProductLeadBlock = ({
  documentDisplayFormat,
  product,
  sidebarItems,
  pdpSpecificationDescription,
  pdpSpecificationTitle,
  pdpFixingToolTitle,
  pdpFixingToolDescription
}: ProductLeadBlockProps) => {
  const { getMicroCopy } = useSiteContext();

  return (
    <StyledAccordion>
      <StyledAccordionItem key="about-product">
        <GTMAccordionSummary
          gtm={{
            label: getMicroCopy(microCopy.PDP_LEAD_BLOCK_ABOUT),
            id: "selector-tabs1",
            action: "Selector – Tabs"
          }}
          data-testid="aboutTab"
          highlightExpanded
        >
          {getMicroCopy(microCopy.PDP_LEAD_BLOCK_ABOUT)}
        </GTMAccordionSummary>
        <StyledAccordionDetails className={classes["with-paddings"]}>
          <AboutTabContent
            productBenefits={product.productBenefits}
            description={product.description}
            guaranteesAndWarrantiesImages={
              product.guaranteesAndWarrantiesImages
            }
            guaranteesAndWarrantiesLinks={product.guaranteesAndWarrantiesLinks}
            awardsAndCertificateDocuments={
              product.awardsAndCertificateDocuments
            }
            awardsAndCertificateImages={product.awardsAndCertificateImages}
            sidebarItems={sidebarItems}
          />
        </StyledAccordionDetails>
      </StyledAccordionItem>
      <StyledAccordionItem key="technical-specifications">
        <GTMAccordionSummary
          gtm={{
            label: getMicroCopy(
              microCopy.PDP_LEAD_BLOCK_TECHNICAL_SPECIFICATIONS
            ),
            id: "selector-tabs1",
            action: "Selector – Tabs"
          }}
          data-testid="technicalSpecificationsTab"
          highlightExpanded
        >
          {getMicroCopy(microCopy.PDP_LEAD_BLOCK_TECHNICAL_SPECIFICATIONS)}
        </GTMAccordionSummary>
        <StyledAccordionDetails className={classes["with-paddings"]}>
          <TechnicalSpecifications
            product={product}
            sidebarItems={sidebarItems}
          />
        </StyledAccordionDetails>
      </StyledAccordionItem>
      {product.productDocuments.length ? (
        <StyledAccordionItem key="documents">
          <GTMAccordionSummary
            gtm={{
              label: getMicroCopy(microCopy.PDP_LEAD_BLOCK_DOCUMENTS),
              id: "selector-tabs1",
              action: "Selector – Tabs"
            }}
            data-testid="documentsTab"
            highlightExpanded
          >
            {getMicroCopy(microCopy.PDP_LEAD_BLOCK_DOCUMENTS)}
          </GTMAccordionSummary>
          <StyledAccordionDetails>
            <ProductDocuments
              productDocuments={product.productDocuments}
              documentDisplayFormat={documentDisplayFormat}
            />
          </StyledAccordionDetails>
        </StyledAccordionItem>
      ) : (
        <></>
      )}
      {product.bimIframeUrl ? (
        <StyledAccordionItem key="bim-iframe">
          <GTMAccordionSummary
            gtm={{
              label: getMicroCopy(microCopy.PDP_LEAD_BLOCK_BIM),
              id: "selector-tabs1",
              action: "Selector – Tabs"
            }}
            data-testid="bimIframeTab"
            highlightExpanded
          >
            {getMicroCopy(microCopy.PDP_LEAD_BLOCK_BIM)}
          </GTMAccordionSummary>
          <StyledAccordionDetails className={classes["with-paddings"]}>
            <AssetsIframe url={product.bimIframeUrl} title="BIM iFrame" />
          </StyledAccordionDetails>
        </StyledAccordionItem>
      ) : (
        <></>
      )}
      {product.techDrawings.length ? (
        <StyledAccordionItem key="tech-drawings">
          <GTMAccordionSummary
            gtm={{
              label: getMicroCopy(microCopy.PDP_LEAD_BLOCK_TECHNICAL_DRAWINGS),
              id: "selector-tabs1",
              action: "Selector – Tabs"
            }}
            data-testid="technicalDrawings"
            highlightExpanded
          >
            {getMicroCopy(microCopy.PDP_LEAD_BLOCK_TECHNICAL_DRAWINGS)}
          </GTMAccordionSummary>
          <StyledAccordionDetails className={classes["with-paddings"]}>
            <MediaGallery
              media={transformImages(product.techDrawings)}
              videoButtonLabel={getMicroCopy(microCopy.MEDIA_VIDEO)}
              visualiserButtonLabel={getMicroCopy(microCopy.MEDIA_3D)}
              visualiserText={getMicroCopy(microCopy.MEDIA_VISUALIZER_TEXT)}
            />
          </StyledAccordionDetails>
        </StyledAccordionItem>
      ) : (
        <></>
      )}
      {product.specificationIframeUrl ? (
        <StyledAccordionItem key="specification-iframe">
          <GTMAccordionSummary
            gtm={{
              label: getMicroCopy(microCopy.PDP_LEAD_BLOCK_SPECIFICATION),
              id: "selector-tabs1",
              action: "Selector – Tabs"
            }}
            data-testid="specification"
            highlightExpanded
          >
            {getMicroCopy(microCopy.PDP_LEAD_BLOCK_SPECIFICATION)}
          </GTMAccordionSummary>
          <StyledAccordionDetails className={classes["with-paddings"]}>
            <SpecificationIframe
              specificationIframeUrl={product.specificationIframeUrl}
              pdpSpecificationDescription={pdpSpecificationDescription}
              pdpSpecificationTitle={pdpSpecificationTitle}
            />
          </StyledAccordionDetails>
        </StyledAccordionItem>
      ) : (
        <></>
      )}
      {product.fixingToolIframeUrl ? (
        <StyledAccordionItem key="fixint-tool-iframe">
          <GTMAccordionSummary
            gtm={{
              label: getMicroCopy(microCopy.PDP_LEAD_BLOCK_FIXING_TOOL),
              id: "selector-tabs1",
              action: "Selector – Tabs"
            }}
            data-testid="fixingTool"
            highlightExpanded
          >
            {getMicroCopy(microCopy.PDP_LEAD_BLOCK_FIXING_TOOL)}
          </GTMAccordionSummary>
          <StyledAccordionDetails className={classes["with-paddings"]}>
            <FixingToolIframe
              fixingToolIframeUrl={product.fixingToolIframeUrl}
              pdpFixingToolTitle={pdpFixingToolTitle}
              pdpFixingToolDescription={pdpFixingToolDescription}
            />
          </StyledAccordionDetails>
        </StyledAccordionItem>
      ) : (
        <></>
      )}
    </StyledAccordion>
  );
};

export default MobileProductLeadBlock;
